import { DateTime } from '@ksyos/date-time';
import { Avatar, Badge, Box, Card, Collection, CollectionItem, CollectionLink, Heading } from '@ksyos/design-system';
import { useLocation, useNavigate } from '@ksyos/router';
import { groupBy, isEmpty, orderBy } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import type { AccountRequestApiResult } from '@common/interfaces/api/accountRequestsApi';
import type { EmploymentWithPractitionerAndOrganizationApiResult } from '@frontend/services/api/employmentsApiService';
import { ManagerAuthorization, PractitionerRole, Profession } from '@common/enums';
import { AccountRequestStatus } from '@common/enums/accountRequestStatus';
import { formatString } from '@common/utils/formatString';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { EmptyListPlaceholder } from '@frontend/components/elements/EmptyListPlaceholder';
import { getAccountRequestsForOrganization } from '@frontend/services/api/accountRequestsApiService';
import { fetchEmploymentsByOrganizationId } from '@frontend/services/api/employmentsApiService';
import { getLanguageCode, getSelectedLanguage } from '@frontend/translate';
import { hasAccountRequestAuthorization, hasOneAuthorization } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { AccountRequestsCollection } from './accountRequests/AccountRequestsCollection';

interface IOrganizationDetailsProps {
    avatarSize?: number;
    displayPractitionerStatusBadge?: boolean;
    isKsyos: boolean;
    isPractitionerListInSidebar?: boolean;
    onClick?: (url: string) => void;
    organizationId: string;
    padding?: 'small' | 'medium';
    shadow?: boolean;
}

interface IEmploymentsGroupedByProfession {
    [profession: string]: EmploymentWithPractitionerAndOrganizationApiResult[];
}

export function OrganizationCareProviderDetails(props: Readonly<IOrganizationDetailsProps>) {
    const abortSignal = useAbortSignal();
    const hasManagerAuthorization = !props.isKsyos && hasOneAuthorization(ManagerAuthorization.employmentSettingsRead);
    const navigate = useNavigate();
    const { hash } = useLocation();
    const language = getSelectedLanguage();

    const [activeEmploymentsGroupedByProfession, setActiveEmploymentsGroupedByProfession] =
        useState<IEmploymentsGroupedByProfession>({});
    const [futureEmployments, setFutureEmployments] = useState<EmploymentWithPractitionerAndOrganizationApiResult[]>(
        []
    );
    const [inactiveEmployments, setInactiveEmployments] = useState<
        EmploymentWithPractitionerAndOrganizationApiResult[]
    >([]);
    const [accountRequests, setAccountRequests] = useState<AccountRequestApiResult[]>([]);

    if (!props.organizationId) {
        return null;
    }

    useEffect(() => {
        const fetchData = async () => {
            await loadEmployments(props.organizationId, props.displayPractitionerStatusBadge);
            await loadAccountRequests(props.organizationId);

            scrollIntoView();
        };

        fetchData();
    }, [props.organizationId, props.displayPractitionerStatusBadge]);

    return (
        <>
            {isEmpty(activeEmploymentsGroupedByProfession) && (
                <EmptyListPlaceholder
                    doNotWrapInCard={props.shadow ? false : true}
                    subtitle={language.organization.noActiveCareProviders}
                />
            )}
            {!props.isPractitionerListInSidebar && Boolean(accountRequests.length) && (
                <AccountRequestsCollection
                    accountRequests={accountRequests}
                    avatarSize={props.avatarSize}
                    isKsyos={props.isKsyos}
                    organizationId={props.organizationId}
                    padding={props.padding}
                    shadow={props.shadow}
                />
            )}
            {Object.keys(activeEmploymentsGroupedByProfession).map((profession) => (
                <Fragment key={profession}>
                    <Heading
                        key={`heading-${profession}`}
                        mb={4}
                        mt={8}
                        size="small"
                    >
                        {language.professionEnumPlural[profession as keyof typeof language.professionEnumPlural]}
                    </Heading>

                    {(() => {
                        const collection = (
                            <Collection
                                key={`collection-${profession}`}
                                type={hasManagerAuthorization ? 'nav' : 'ul'}
                            >
                                {activeEmploymentsGroupedByProfession[profession].map((employment) =>
                                    renderEmployment(employment)
                                )}
                            </Collection>
                        );

                        return props.shadow ? <Card key={`card-${profession}`}>{collection}</Card> : collection;
                    })()}
                </Fragment>
            ))}
            {props.isPractitionerListInSidebar && Boolean(futureEmployments.length) && (
                <Fragment key={'future'}>
                    <Heading
                        mb={4}
                        mt={8}
                        size="small"
                    >
                        {language.organization.futureEmploymentsTitle}
                    </Heading>
                    {props.shadow ? (
                        <Card>
                            <Collection type={hasManagerAuthorization ? 'nav' : 'ul'}>
                                {futureEmployments.map((employment) => renderEmployment(employment))}
                            </Collection>
                        </Card>
                    ) : (
                        <Collection type={hasManagerAuthorization ? 'nav' : 'ul'}>
                            {futureEmployments.map((employment) => renderEmployment(employment))}
                        </Collection>
                    )}
                </Fragment>
            )}
            {!props.isPractitionerListInSidebar && Boolean(inactiveEmployments.length) && (
                <Fragment key={'inactive'}>
                    <Heading
                        mb={4}
                        mt={8}
                        size="small"
                    >
                        {language.organization.inactiveEmploymentsTitle}
                    </Heading>
                    {props.shadow ? (
                        <Card>
                            <Collection type={hasManagerAuthorization ? 'nav' : 'ul'}>
                                {inactiveEmployments.map((employment) => renderEmployment(employment))}
                            </Collection>
                        </Card>
                    ) : (
                        <Collection type={hasManagerAuthorization ? 'nav' : 'ul'}>
                            {inactiveEmployments.map((employment) => renderEmployment(employment))}
                        </Collection>
                    )}
                </Fragment>
            )}
        </>
    );

    function renderEmployment(practitioner: EmploymentWithPractitionerAndOrganizationApiResult) {
        return hasManagerAuthorization ? renderLinkItem(practitioner) : renderItem(practitioner);
    }

    function renderItem(employment: EmploymentWithPractitionerAndOrganizationApiResult) {
        return (
            <CollectionItem
                key={employment.practitionerId}
                mediaItem={
                    <Avatar
                        imageUrl={employment.practitioner.profilePictureUrl}
                        initials={getDisplayInitials(employment.practitioner)}
                        key={`avatar-${employment.practitionerId}`}
                        size={props.avatarSize}
                    />
                }
                padding={props.padding}
                title={getDisplayName(employment.practitioner, false, 'nameGivenNameFamily')}
            >
                {props.displayPractitionerStatusBadge && getStatusBadgeForOrganization(employment)}
                {renderPayoutManagerBadge(employment)}
            </CollectionItem>
        );
    }

    function renderLinkItem(employment: EmploymentWithPractitionerAndOrganizationApiResult) {
        return (
            <CollectionLink
                id={employment.practitionerId}
                key={employment.practitionerId}
                mediaItem={
                    <Avatar
                        imageUrl={employment.practitioner.profilePictureUrl}
                        initials={getDisplayInitials(employment.practitioner)}
                        key={`avatar-${employment.practitionerId}`}
                        size={props.avatarSize}
                    />
                }
                onClick={() => {
                    if (props.onClick) {
                        props.onClick(`/employments/${employment.id}/personalSettings`);
                    } else {
                        redirectToProfilePage(employment);
                    }
                }}
                padding={props.padding}
                subtitle={getSubtitle(employment)}
                title={getDisplayName(employment.practitioner, false, 'nameGivenNameFamily')}
            >
                {props.displayPractitionerStatusBadge && renderStatusBadgesForKsyosInternal(employment)}
            </CollectionLink>
        );
    }

    function redirectToProfilePage(employment: EmploymentWithPractitionerAndOrganizationApiResult) {
        navigate(`/employments/${employment.id}/personalSettings`);
    }

    async function loadEmployments(organizationId: string, displayPractitionerStatusBadge: boolean = false) {
        const employments = await fetchEmploymentsByOrganizationId(
            organizationId,
            displayPractitionerStatusBadge,
            abortSignal
        );

        const getProfessionSortOrder = (profession: Profession) => {
            switch (profession) {
                case Profession.generalPractitioner:
                    return 0;
                case Profession.generalPractitionerMentalHealthSpecialist:
                    return 1;
                case Profession.generalPractitionerHidha:
                    return 2;
                case Profession.generalPractitionerAios:
                    return 3;
                case Profession.generalPractitionerWaarnemer:
                    return 4;
                case Profession.other:
                    return 99;
                default:
                    return 10;
            }
        };

        const inactiveEmployments = employments.filter(
            (employment) => !!employment.endDate && DateTime.from(employment.endDate).isBefore(new Date())
        );
        const nowAndFutureActiveEmployments = employments.filter(
            (employment) => !inactiveEmployments.includes(employment)
        );
        const futureActiveEmployments = nowAndFutureActiveEmployments.filter((employment) =>
            DateTime.from(employment.startDate).isAfter(new Date())
        );
        const nowActiveEmployments = nowAndFutureActiveEmployments.filter(
            (employment) => !futureActiveEmployments.includes(employment)
        );

        const orderedEmployments = orderBy(
            props.isPractitionerListInSidebar ? nowActiveEmployments : nowAndFutureActiveEmployments,
            (employment) => getProfessionSortOrder(employment.profession)
        );
        const groupedEmployments = groupPractitionersByProfession(orderedEmployments);

        setActiveEmploymentsGroupedByProfession(groupedEmployments);
        setInactiveEmployments(inactiveEmployments);
        setFutureEmployments(futureActiveEmployments);
    }

    async function loadAccountRequests(organizationId: string) {
        const hasReadAuthorization = hasAccountRequestAuthorization('read');

        if (hasReadAuthorization) {
            const isKsyosUserWithAuthorization = hasAccountRequestAuthorization('read', true);
            const organizationAccountRequests = await getAccountRequestsForOrganization(organizationId, abortSignal);

            const accountRequestStatusesToFilter = isKsyosUserWithAuthorization
                ? [AccountRequestStatus.pending, AccountRequestStatus.rejected]
                : [AccountRequestStatus.pending];

            const filteredAccountRequests = organizationAccountRequests.items.filter(({ status }) =>
                accountRequestStatusesToFilter.includes(status)
            );

            const sortedAccountRequests = sortAccountRequests(filteredAccountRequests);

            setAccountRequests(sortedAccountRequests);
        } else {
            setAccountRequests([]);
        }
    }

    function groupPractitionersByProfession(
        employmentsToBeGrouped: EmploymentWithPractitionerAndOrganizationApiResult[]
    ): IEmploymentsGroupedByProfession {
        return groupBy(employmentsToBeGrouped, (employment) => employment.profession);
    }

    function renderStatusBadgesForKsyosInternal(employment: EmploymentWithPractitionerAndOrganizationApiResult) {
        const generalPractitionerProfessions = [
            Profession.generalPractitioner,
            Profession.generalPractitionerMentalHealthSpecialist,
            Profession.generalPractitionerHidha,
            Profession.generalPractitionerAios,
            Profession.generalPractitionerWaarnemer,
        ];

        const badges: JSX.Element[] = getStatusBadgeForOrganization(employment)
            ? [getStatusBadgeForOrganization(employment) as JSX.Element]
            : [];

        if (!badges.length) {
            if (!employment.hasActiveRights) {
                badges.push(
                    <Badge
                        color="orange"
                        key={`employment-status-${employment.id}`}
                        size="medium"
                        text={language.practitionerListStatusBadges.noCarePathRights}
                    />
                );
            }

            if (!employment.canLoginToCs) {
                badges.push(
                    <Badge
                        color="neutral"
                        key={`employment-status-${employment.id}`}
                        size="medium"
                        text={language.practitionerListStatusBadges.accountWithoutLogin}
                    />
                );
            } else if (generalPractitionerProfessions.includes(employment.profession) && !employment.zorgmailNummer) {
                badges.push(
                    <Badge
                        color="neutral"
                        key={`employment-status-${employment.id}`}
                        size="medium"
                        text={language.practitionerListStatusBadges.noZorgmail}
                    />
                );
            }
        }

        return (
            <Box
                display="flex"
                gap={[1, 1]}
            >
                {badges}
            </Box>
        );
    }

    function getStatusBadgeForOrganization(employment: EmploymentWithPractitionerAndOrganizationApiResult) {
        if (employment.endDate && DateTime.from(employment.endDate).isBefore(DateTime.now())) {
            return (
                <Badge
                    color="neutral"
                    key={`employment-status-${employment.id}`}
                    size="medium"
                    text={formatString(language.practitionerListStatusBadges.endedBy, [
                        DateTime.from(employment.endDate).format('DD-MM-YYYY'),
                    ])}
                />
            );
        } else if (shouldShowActivationEmailBadge(employment)) {
            return (
                <Badge
                    color="neutral"
                    key={`employment-status-${employment.id}`}
                    size="medium"
                    text={getActivationEmailSentAtMessage(
                        employment.practitioner.activationEmailSentAt ?? employment.startDate
                    )}
                />
            );
        }
    }

    function renderPayoutManagerBadge(employment: EmploymentWithPractitionerAndOrganizationApiResult) {
        if (employment?.employmentPractitionerRoles.includes(PractitionerRole.organizationPayoutManager)) {
            return (
                <Badge
                    color="neutral"
                    key={`employment-status-${employment.id}`}
                    size="medium"
                    text={language.employment.organizationManager}
                />
            );
        }
    }

    function getActivationEmailSentAtMessage(activationEmailSendDate: string | Date) {
        if (DateTime.from(activationEmailSendDate).isSameOrBefore(DateTime.now())) {
            return formatString(language.practitionerListStatusBadges.activationEmailWasSentOn, [
                DateTime.from(activationEmailSendDate).format('DD-MM-YYYY'),
            ]);
        } else {
            return formatString(language.practitionerListStatusBadges.activationEmailIsGoingToBeSentOn, [
                DateTime.from(activationEmailSendDate).format('DD-MM-YYYY'),
            ]);
        }
    }

    function scrollIntoView() {
        const id = hash.replace('#', '');

        if (id) {
            const element = document.getElementById(id);

            if (element) {
                element.scrollIntoView();
            }
        }
    }

    function sortAccountRequests(filteredAccountRequests: AccountRequestApiResult[]) {
        // We want to show pending account request first and then rejected ones sort by created_at
        return [...filteredAccountRequests].sort((a: AccountRequestApiResult, b: AccountRequestApiResult) => {
            if (a.status === AccountRequestStatus.pending && b.status !== AccountRequestStatus.pending) {
                return -1;
            } else if (a.status !== AccountRequestStatus.pending && b.status === AccountRequestStatus.pending) {
                return 1;
            } else {
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            }
        });
    }

    function getSubtitle({ practitioner, specialism }: EmploymentWithPractitionerAndOrganizationApiResult) {
        const selectedLanguage = getLanguageCode().toUpperCase();

        const uid = practitioner.uid ? practitioner.uid.toString() : '';

        const specialismDescription = specialism
            ? specialism[
                  `description${selectedLanguage}` as keyof Pick<typeof specialism, 'descriptionEN' | 'descriptionNL'>
              ]
            : '';

        if (uid && specialismDescription) {
            return `${uid} - ${specialismDescription}`;
        } else {
            return uid ?? specialismDescription;
        }
    }

    function shouldShowActivationEmailBadge(employment: EmploymentWithPractitionerAndOrganizationApiResult) {
        return (
            !employment.practitioner.isActivated &&
            employment.practitioner.emailAddress &&
            (employment.practitioner.activationEmailSentAt ||
                DateTime.from(employment.startDate).isSameOrAfter(DateTime.today()))
        );
    }
}
